import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, getDocs, where, doc, getDoc  } from '../firebaseConfig';
import { Bar } from 'react-chartjs-2';
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import Divider from "@mui/material/Divider";
import TimelineIcon from '@mui/icons-material/Timeline';
import MDTypography from "components/MDTypography";
import { MenuItem, Select } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

const TradingSessionChart = ({ userID, tradeAccountID = 'default' }) => {
  const [trades, setTrades] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [accountName, setAccountName] = useState(tradeAccountID); // Default to ID
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];
  
    // Fetch account name on mount
    useEffect(() => {
      const fetchAccountName = async () => {
        if (tradeAccountID === 'default') {
          setAccountName('Default');
          return;
        }
        try {
          const accountDoc = await getDoc(doc(db, 'Users', userID, 'tradeAccounts', tradeAccountID));
          if (accountDoc.exists()) {
            setAccountName(accountDoc.data().name || tradeAccountID);
          }
        } catch (error) {
          console.error('Error fetching account name:', error);
        }
      };
      fetchAccountName();
    }, [userID, tradeAccountID]);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(
          collection(db, tradesPath),
          where('status', '==', 'Closed')
        );
        const tradeSnapshot = await getDocs(tradesQuery);
        const tradesData = tradeSnapshot.docs.map(doc => ({
          id: doc.id,
          accountID: tradeAccountID,
          ...doc.data()
        }));
        setTrades(tradesData);
      } catch (error) {
        console.error('Error fetching trades for TradingSessionChart:', error);
      }
    };
    fetchTrades();
  }, [userID, tradeAccountID]);

  const calculateSessionData = () => {
    const sessions = {
      Asian: { hours: [0, 8], returns: 0 },
      European: { hours: [8, 16], returns: 0 },
      US: { hours: [16, 24], returns: 0 },
    };

    trades
      .filter(trade => new Date(trade.date).getFullYear() === year)
      .forEach(trade => {
        const entryHour = new Date(`${trade.date}T${trade.entryTime}`).getUTCHours();
        for (const [session, { hours }] of Object.entries(sessions)) {
          if (entryHour >= hours[0] && entryHour < hours[1]) {
            sessions[session].returns += parseFloat(trade.returns || 0);
          }
        }
      });

    return {
      labels: Object.keys(sessions),
      datasets: [{
        label: 'Returns ($)',
        data: Object.values(sessions).map(s => s.returns),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      }],
    };
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: { y: { title: { display: true, text: 'Total Returns ($)' } } },
  };

  const handleYearChange = (event) => setYear(event.target.value);

  const tooltipContent = `This chart compares your performance across market sessions (Asian: 00-08 UTC, European: 08-16 UTC, US: 16-24 UTC) based on entry time, showing total returns per session.`;

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox variant="gradient" bgColor="light" color="dark" coloredShadow="light" borderRadius="xl" display="flex" justifyContent="center" alignItems="center" width="4rem" height="4rem" mt={-3}>
            <TimelineIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Trading Session Analysis {tradeAccountID === 'default' ? '' : `(${accountName})`}
            </MDTypography>
            <Tooltip title={tooltipContent} placement="bottom-end" componentsProps={{ tooltip: { sx: { bgcolor: theme.palette.background.paper, color: theme.palette.text.primary, border: `1px solid ${theme.palette.divider}`, p: 1.5, maxWidth: '250px', fontSize: '0.75rem', lineHeight: 1.5 } } }}>
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="center" mt={2}>
          <Select value={year} onChange={handleYearChange}>
            {[2024, 2025, 2026].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </MDBox>
        <MDBox height="100%">
          <Bar data={calculateSessionData()} options={chartOptions} />
        </MDBox>
      </MDBox>
    </Card>
  );
};

TradingSessionChart.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string,
};

export default TradingSessionChart;